import React, { useState, useEffect, useRef } from 'react';
import { Steps, Card, Button, Form, Input, Upload, message, Spin, Result, Space, Tag, Row, Col } from 'antd';
import { InboxOutlined, CheckCircleOutlined, UserOutlined, FormOutlined, FileTextOutlined, CameraOutlined, PictureOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { essayAPI, correctionAPI, topicAPI } from '../services/api';
import { ProgressManager, ProgressInfo } from '../services/progressManager';
import { } from '../types';
import StudentSelector from '../components/StudentSelector';
import TopicSelector from '../components/TopicSelector';
import CorrectionResultDisplay from '../components/CorrectionResultDisplay';
import ProgressDisplayFull from '../components/ProgressDisplayFull';
import { extractTopicData, getTopicFromEssay } from '../utils/topicHelper';
import { compressImageToFile, formatFileSize, calculateCompressionRatio } from '../utils/imageCompress';
import './EssayWizard.css';

const { Step } = Steps;
const { TextArea } = Input;
const { Dragger } = Upload;

const EssayWizard: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [essayData, setEssayData] = useState<any>({});
  const [studentInfo, setStudentInfo] = useState<any>({});
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);  // 支持多文件
  const [ocrText, setOcrText] = useState('');
  const [correctionResult, setCorrectionResult] = useState<any>(null);
  const [progressInfo, setProgressInfo] = useState<ProgressInfo | null>(null);
  const [showProgress, setShowProgress] = useState(false);
  const [isResumeMode, setIsResumeMode] = useState(false);
  const uploadAreaRef = useRef<HTMLDivElement>(null);
  const progressManagerRef = useRef<ProgressManager>(new ProgressManager());
  const [form] = Form.useForm();

  const steps = [
    { title: '选择学生', description: '选择或添加学生', icon: <UserOutlined /> },
    { title: '设定题目', description: '设置作文题目和要求', icon: <FormOutlined /> },
    { title: '上传作文', description: '上传学生作文文件', icon: <InboxOutlined /> },
    { title: '确认内容', description: '确认识别的文本内容', icon: <FileTextOutlined /> },
    { title: '查看结果', description: '查看批改结果', icon: <CheckCircleOutlined /> },
  ];

  // 初始化：检查是否是恢复模式
  useEffect(() => {
    const initializeWizard = async () => {
      // 判断是否为恢复模式
      const isResume = location.pathname.includes('/continue/');
      const resumeEssayId = params.id;
      const searchParams = new URLSearchParams(location.search);
      const resumeStep = searchParams.get('step');
      
      if (isResume && resumeEssayId) {
        setIsResumeMode(true);
        setLoading(true);
        
        try {
          // 加载已有的作文数据和工作流状态
          const response = await essayAPI.get(parseInt(resumeEssayId));
          const { essay, workflow } = response.data;

          console.log('Resume mode - essay data:', essay); // 调试日志

          // 使用工具函数处理 topic 数据
          let topicData = getTopicFromEssay(essay);

          if (!topicData && essay.topic_id) {
            try {
              const topicResponse = await topicAPI.get(essay.topic_id);
              topicData = extractTopicData(topicResponse.data.data);
              console.log('Loaded topic data separately:', topicData);
            } catch (err) {
              console.error('Failed to load topic:', err);
            }
          }

          // 设置作文数据
          setEssayData({
            id: essay.id,
            topic_id: essay.topic_id,
            topic: topicData, // 使用加载的题目数据
            original_file: essay.original_file,
            original_text: essay.original_text,
            corrected_text: essay.corrected_text,
            status: essay.status,
            needs_ocr: essay.needs_ocr,
            last_error: essay.last_error
          });
          
          // 根据工作流状态恢复数据
          if (workflow.steps_completed.student_selected) {
            setStudentInfo({
              studentId: essay.student_id,
              studentName: essay.student?.name || '',
              classId: essay.class_id,
              className: essay.class?.name || ''
            });
          }
          
          if (workflow.steps_completed.file_uploaded) {
            setUploadedFile({ name: essay.original_file || '已上传文件' });
          }
          
          if (workflow.steps_completed.text_confirmed) {
            setOcrText(essay.corrected_text || essay.original_text || '');
          }
          
          if (essay.correction) {
            setCorrectionResult(essay.correction);
          }
          
          // 设置当前步骤
          const targetStep = resumeStep ? parseInt(resumeStep) : workflow.current_step;
          setCurrent(targetStep);
          
          // 如果正在处理中，显示进度
          if (!workflow.can_continue) {
            setShowProgress(true);
            message.info('作文正在处理中...');
          } else {
            message.success(`已恢复到步骤: ${workflow.next_action}`);
          }
        } catch (error: any) {
          message.error('加载作文数据失败');
          console.error('Failed to load essay:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    initializeWizard();
  }, [location, params]);

  const next = () => {
    setCurrent(current + 1);
  };

  const prev = () => {
    setCurrent(current - 1);
  };

  // 步骤0：选择学生
  const handleStudentSelect = (studentData: any) => {
    if (!studentData.studentId || !studentData.classId) {
      message.error('请选择学生');
      return;
    }
    setStudentInfo(studentData);
    message.success(`已选择学生: ${studentData.studentName}`);
    next();
  };

  // 步骤1：创建作文
  const handleStep1Submit = async (values: any) => {
    setLoading(true);

    try {
      // 获取选中题目的完整信息
      const selectedTopic = await topicAPI.get(values.topic_id);
      const topicData = extractTopicData(selectedTopic.data.data);

      console.log('Selected topic data:', topicData); // 调试日志

      if (!topicData) {
        message.error('无法获取题目信息');
        setLoading(false);
        return;
      }

      // 使用题目自带的essay_type，而不是表单中的
      const finalValues = {
        topic_id: values.topic_id,
        essay_type: topicData.essay_type,
      };

      // 恢复模式下，更新作文信息
      if (isResumeMode && essayData.id) {
        await essayAPI.update(essayData.id, finalValues);
        // 确保使用完整的topic数据
        const updatedEssayData = {
          ...essayData,
          ...finalValues,
          topic: topicData  // 设置完整的题目数据
        };
        console.log('Setting essayData with topic:', updatedEssayData); // 调试日志
        setEssayData(updatedEssayData);
        message.success('作文信息已更新');
        next();
        setLoading(false);
        return;
      }

      if (!studentInfo.studentId || !studentInfo.classId) {
        message.error('请先选择学生');
        setCurrent(0);
        setLoading(false);
        return;
      }

      const response = await essayAPI.createWithStudent({
        ...finalValues,
        student_id: studentInfo.studentId,
        class_id: studentInfo.classId,
      });
      // 合并响应数据，确保包含完整的topic数据
      const essayDataWithTopic = {
        ...response.data.essay,
        topic: topicData  // 直接使用我们获取的完整topicData
      };
      setEssayData(essayDataWithTopic);
      message.success('作文创建成功');
      next();
    } catch (error: any) {
      message.error(error.response?.data?.error || '处理作文失败');
    } finally {
      setLoading(false);
    }
  };

  // 步骤2：上传文件（支持单个或多个文件）
  const handleUpload = async (file: File, fileList?: File[]) => {
    if (!essayData.id) {
      message.error('请先设置作文题目');
      return false;
    }

    setLoading(true);
    setShowProgress(true);

    // 启动进度轮询
    const manager = progressManagerRef.current;
    manager.onProgress((progress) => {
      setProgressInfo(progress);
    });
    manager.onError((error) => {
      console.error('进度轮询失败:', error);
    });

    try {
      // 开始轮询进度
      manager.startPolling(essayData.id, { interval: 1000, immediate: true });

      // 判断是否是多文件上传
      if (fileList && fileList.length > 1) {
        if (fileList.some((item) => !item.type.startsWith('image/'))) {
          message.error('多文件上传仅支持图片，请单独上传 PDF/Word/文本文件');
          manager.stopPolling();
          setShowProgress(false);
          setLoading(false);
          return false;
        }

        // 多文件上传
        message.info(`正在压缩和上传 ${fileList.length} 张图片...`);

        // 压缩所有图片
        const compressedFiles: File[] = [];
        let originalTotalSize = 0;
        let compressedTotalSize = 0;

        for (let i = 0; i < fileList.length; i++) {
          const originalFile = fileList[i];
          originalTotalSize += originalFile.size;

          // 只压缩图片文件
          if (originalFile.type.startsWith('image/')) {
            const compressedFile = await compressImageToFile(originalFile, {
              maxWidth: 1600,
              maxHeight: 1600,
              quality: 0.85
            });
            compressedFiles.push(compressedFile);
            compressedTotalSize += compressedFile.size;
          } else {
            compressedFiles.push(originalFile);
            compressedTotalSize += originalFile.size;
          }
        }

        // 显示压缩效果
        if (originalTotalSize > compressedTotalSize) {
          const ratio = calculateCompressionRatio(originalTotalSize, compressedTotalSize);
          message.success({
            content: `压缩完成：${formatFileSize(originalTotalSize)} → ${formatFileSize(compressedTotalSize)} (节省 ${ratio}%)`,
            duration: 3
          });
        }

        // 上传多个文件
        const uploadResponse = await essayAPI.uploadMultiple(essayData.id, compressedFiles);
        setUploadedFiles(compressedFiles);
        setUploadedFile(compressedFiles[0]); // 兼容旧逻辑

        message.success(`成功上传 ${fileList.length} 张图片`);

        // 需要OCR识别
        if (uploadResponse.data.needs_ocr !== false) {
          const ocrResponse = await essayAPI.ocr(essayData.id);
          setOcrText(ocrResponse.data.text || '识别中...');
          message.success('正在识别多张图片中的文字...');
        }
      } else {
        // 单文件上传（保持原有逻辑）
        let uploadFile = file;

        // 如果是图片，先压缩
        if (file.type.startsWith('image/')) {
          const originalSize = file.size;
          uploadFile = await compressImageToFile(file, {
            maxWidth: 1600,
            maxHeight: 1600,
            quality: 0.85
          });
          const compressedSize = uploadFile.size;

          if (originalSize > compressedSize) {
            const ratio = calculateCompressionRatio(originalSize, compressedSize);
            message.success({
              content: `图片压缩：${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)} (节省 ${ratio}%)`,
              duration: 3
            });
          }
        }

        const uploadResponse = await essayAPI.upload(essayData.id, uploadFile);
        setUploadedFile(uploadFile);

        // 检查是否需要OCR
        if (uploadResponse.data.needs_ocr === false && uploadResponse.data.text) {
          // 文档文件，已直接提取文本
          setOcrText(uploadResponse.data.text || '');
          message.success('文档上传成功，文本已提取');
        } else if (uploadResponse.data.manual_input_required) {
          setOcrText('');
          message.warning(uploadResponse.data.message || '文档上传成功，但文本提取失败，请手动输入');
        } else if (uploadResponse.data.needs_ocr) {
          // 图片文件，需要OCR识别
          const ocrResponse = await essayAPI.ocr(essayData.id);
          setOcrText(ocrResponse.data.text || '识别中...');
          message.success('文件上传成功，正在识别文字...');
        } else {
          // 未知情况，尝试OCR
          try {
            const ocrResponse = await essayAPI.ocr(essayData.id);
            setOcrText(ocrResponse.data.text || '识别中...');
            message.success('文件上传成功，正在识别文字...');
          } catch (e) {
            message.warning('文件上传成功，但文本提取失败，请手动输入');
          }
        }
      }

      // OCR完成后停止轮询
      manager.stopPolling();
      setShowProgress(false);
      next();
    } catch (error: any) {
      message.error(error.response?.data?.error || '文件上传失败');
      manager.stopPolling();
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
    return false;
  };

  // 处理粘贴事件
  const handlePaste = async (e: ClipboardEvent) => {
    e.preventDefault();
    
    const items = e.clipboardData?.items;
    if (!items) return;
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const newFile = new File([file], `paste-image-${Date.now()}.png`, {
            type: file.type,
          });
          
          message.info('正在处理粘贴的图片...');
          await handleUpload(newFile, [newFile]);
        }
        break;
      }
    }
  };

  // 步骤3：提交批改 - 完全依赖后端状态
  const handleStep3Submit = async () => {
    if (!ocrText) {
      message.error('请确认识别的文本内容');
      return;
    }

    setLoading(true);
    setShowProgress(true);

    // 启动进度轮询
    const manager = progressManagerRef.current;

    try {
      // 重新开始轮询进度（从AI分析阶段开始）
      manager.startPolling(essayData.id, { interval: 1000, immediate: true });

      // 更新作文文本
      await essayAPI.update(essayData.id, {
        corrected_text: ocrText,
      });

      // 验证更新是否成功
      const updatedEssay = await essayAPI.get(essayData.id);
      if (!updatedEssay.data.essay.corrected_text && !updatedEssay.data.essay.original_text) {
        throw new Error('作文内容更新失败');
      }

      // 开始批改
      await essayAPI.correct(essayData.id);

      // 等待批改完成
      await new Promise(resolve => setTimeout(resolve, 1000));

      // 从后端获取批改结果
      const correctionResponse = await correctionAPI.get(essayData.id);

      // 再次获取作文最新状态
      const finalEssayData = await essayAPI.get(essayData.id);

      // 直接使用后端返回的数据，不要构造新的数据
      const formattedResult = {
        correction: correctionResponse.data.correction,
        errors: correctionResponse.data.errors || [],
        essay: finalEssayData.data.essay,
        dimension_scores: correctionResponse.data.dimension_scores
      };

      console.log('从后端获取的批改结果:', formattedResult);

      setCorrectionResult(formattedResult);

      // 根据后端状态判断是否成功
      if (finalEssayData.data.essay.status === 'completed') {
        message.success('批改完成！');
        // 批改完成后停止轮询
        manager.stopPolling();
        setShowProgress(false);
        next();
      } else {
        throw new Error('批改未完成');
      }
    } catch (error: any) {
      console.error('批改失败:', error);

      // 即使失败也要从后端获取最新状态
      try {
        const latestEssay = await essayAPI.get(essayData.id);
        setEssayData(latestEssay.data.essay);
      } catch {
        // 忽略获取状态的错误
      }

      message.error(error.response?.data?.error || error.message || '批改失败');
      manager.stopPolling();
      setShowProgress(false);
    } finally {
      setLoading(false);
    }
  };

  // 注册粘贴事件监听
  useEffect(() => {
    if (current === 2 && uploadAreaRef.current) {
      const handlePasteWrapper = (e: Event) => handlePaste(e as ClipboardEvent);
      // 在 effect 内部保存 ref 引用
      const uploadArea = uploadAreaRef.current;
      uploadArea.addEventListener('paste', handlePasteWrapper);

      return () => {
        // 使用保存的引用
        uploadArea.removeEventListener('paste', handlePasteWrapper);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // 清理进度管理器
  useEffect(() => {
    // 在 effect 内部保存 ref 引用
    const managerRef = progressManagerRef;

    return () => {
      // 使用保存的引用
      managerRef.current.destroy();
    };
  }, []);

  const renderStepContent = () => {
    switch (current) {
      case 0:
        return (
          <div className="wizard-step-panel wizard-step-student">
            <h3 className="wizard-step-title">请选择学生</h3>
            
            <StudentSelector
              className="wizard-student-selector"
              onChange={(data) => setStudentInfo(data)}
              placeholder="输入学生姓名搜索或添加新学生"
            />

            {studentInfo.studentName && (
              <div className="wizard-selected-student">
                <Tag color="green" style={{ fontSize: 14, padding: '4px 12px' }}>
                  已选择: {studentInfo.studentName}
                </Tag>
              </div>
            )}

            <div className="wizard-actions wizard-actions-single">
              <Button 
                type="primary" 
                size="large"
                onClick={() => handleStudentSelect(studentInfo)}
                disabled={!studentInfo.studentId}
              >
                下一步
              </Button>
            </div>

            <div className="wizard-tips">
              <p>提示：</p>
              <ul>
                <li>先选择班级，再输入学生姓名</li>
                <li>如果学生不存在，可以快速创建</li>
                <li>学生信息会自动关联到作文</li>
              </ul>
            </div>
          </div>
        );

      case 1:
        return (
          <Form 
            form={form}
            onFinish={handleStep1Submit} 
            layout="vertical"
            className="wizard-step-panel wizard-step-form"
            initialValues={isResumeMode ? {
              topic_id: essayData.topic_id
            } : {}}
          >
            <div style={{ marginBottom: 20 }}>
              <Tag color="blue">学生: {studentInfo.studentName}</Tag>
            </div>

            <Form.Item
              name="topic_id"
              label="选择或创建作文题目"
              rules={[{ required: true, message: '请选择或输入作文题目' }]}
              style={{ marginBottom: 24 }}
            >
              <TopicSelector
                value={form.getFieldValue('topic_id')}
                onChange={(topicId, topic) => {
                  if (topic) {
                    form.setFieldsValue({
                      topic_id: topic.id
                    });
                  }
                }}
                disabled={isResumeMode}
              />
            </Form.Item>

            <Form.Item>
              <Space className="wizard-actions">
                <Button onClick={prev}>
                  上一步
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {isResumeMode ? '下一步' : '创建作文'}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        );

      case 2:
        return (
          <div ref={uploadAreaRef} className="wizard-step-panel wizard-step-upload">
            <div style={{ marginBottom: 20 }}>
              <Space wrap>
                <Tag color="blue">学生: {studentInfo.studentName}</Tag>
                <Tag color="green">题目: {essayData.topic?.title || '未设置'}</Tag>
              </Space>
            </div>

            {showProgress && progressInfo ? (
              <ProgressDisplayFull progress={progressInfo} loading={loading} />
            ) : (
              <>
                {/* 恢复模式下，如果已有文件，显示提示 */}
                {isResumeMode && uploadedFile ? (
                  <div style={{ marginBottom: 20, textAlign: 'center' }}>
                    <Tag color="success" style={{ fontSize: 14, padding: '8px 16px' }}>
                      文件已上传
                    </Tag>
                    {!ocrText && essayData.needs_ocr !== false && (
                      <div style={{ marginTop: 16 }}>
                        <Button type="primary" onClick={async () => {
                          setLoading(true);
                          try {
                            const ocrResponse = await essayAPI.ocr(essayData.id);
                            setOcrText(ocrResponse.data.text || '');
                            message.success('文字识别完成');
                            next();
                          } catch (error) {
                            message.error('文字识别失败，请重新上传');
                          } finally {
                            setLoading(false);
                          }
                        }} loading={loading}>
                          继续识别文字
                        </Button>
                      </div>
                    )}
                    {!ocrText && essayData.needs_ocr === false && (
                      <div style={{ marginTop: 16 }}>
                        <Tag color="warning" style={{ padding: '8px 16px' }}>
                          {essayData.last_error || '该文件无法自动识别，请下一步手动输入内容'}
                        </Tag>
                      </div>
                    )}
                  </div>
                ) : null}
                
                <Dragger
                  className="wizard-upload-dragger"
                  accept="image/*,.pdf,.docx,.doc,.txt"
                  multiple={true}  // 支持多文件选择
                  beforeUpload={(file, fileList) => {
                    handleUpload(file, fileList);
                    return false;
                  }}
                  showUploadList={false}
                  style={{ cursor: 'pointer' }}
                  capture={false}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    {isResumeMode && uploadedFile ? '重新上传文件' : '点击、拖拽或粘贴文件到此处上传'}
                  </p>
                  <p className="ant-upload-hint">
                    支持格式：单文件支持图片（JPG/PNG）、PDF、Word文档（.docx/.doc）、纯文本<br />
                    <strong>多选仅支持图片，自动压缩和合并识别</strong><br />
                    <strong>提示：可以直接使用 Ctrl+V (或 Cmd+V) 粘贴图片</strong>
                  </p>
                </Dragger>
                
                {/* 移动端专用上传按钮 */}
                <div className="mobile-upload-buttons">
                  <Upload
                    accept="image/*"
                    capture="environment"
                    beforeUpload={(file) => {
                      handleUpload(file, [file]);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button 
                      type="primary" 
                      size="large" 
                      icon={<CameraOutlined />}
                      className="mobile-upload-button"
                    >
                      拍照上传
                    </Button>
                  </Upload>
                  
                  <Upload
                    accept="image/*,.pdf,.docx,.doc,.txt"
                    multiple={true}
                    beforeUpload={(file, fileList) => {
                      handleUpload(file, fileList);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button 
                      size="large" 
                      icon={<PictureOutlined />}
                      className="mobile-upload-button"
                    >
                      从相册选择
                    </Button>
                  </Upload>
                </div>
              </>
            )}
            
            {uploadedFile && (
              <div style={{ marginTop: 16 }}>
                <p>
                  已上传: {uploadedFiles.length > 1
                    ? `${uploadedFiles.length} 个文件`
                    : uploadedFile.name}
                </p>
                {uploadedFiles.length > 1 && (
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    {uploadedFiles.map((f, idx) => (
                      <li key={idx}>{f.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            
            <div className="wizard-actions">
              <Space wrap style={{ width: '100%' }}>
                <Button onClick={prev}>
                  上一步
                </Button>
                <Button type="primary" onClick={next} 
                  disabled={!uploadedFile || (isResumeMode && !ocrText && essayData.needs_ocr !== false)}>
                  {isResumeMode && ocrText ? '下一步' : (uploadedFile ? '下一步' : '请上传文件')}
                </Button>
              </Space>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="wizard-step-panel wizard-step-confirm">
            <div style={{ marginBottom: 20 }}>
              <Space>
                <Tag color="blue">学生: {studentInfo.studentName}</Tag>
                <Tag color="green">题目: {essayData.topic?.title || '未设置'}</Tag>
              </Space>
            </div>

            {showProgress && progressInfo ? (
              <ProgressDisplayFull progress={progressInfo} loading={loading} />
            ) : (
              <>
                <h3>请确认识别的文本内容</h3>
                <TextArea
                  className="wizard-confirm-textarea"
                  value={ocrText}
                  onChange={(e) => setOcrText(e.target.value)}
                  rows={15}
                  placeholder="识别的文本内容..."
                />
                
                <div className="wizard-actions">
                  <Button onClick={prev}>
                    上一步
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleStep3Submit}
                    loading={loading}
                  >
                    提交批改
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      case 4:
        return (
          <div className="wizard-step-panel wizard-step-result">
            <Result
              status="success"
              title="批改完成！"
              subTitle={
                <div>
                  <p>学生：{studentInfo.studentName}</p>
                  <p>作文：{essayData.topic?.title || '未设置'}</p>
                </div>
              }
            />
            
            {/* 显示批改结果 */}
            {correctionResult && (
              <div style={{ marginTop: 24 }}>
                <CorrectionResultDisplay 
                  correctionData={correctionResult}
                  showFullDetails={true}
                  showScoreCard={true}
                  showErrorList={false}   // 向导中不显示完整错误列表
                  showTabs={false}        // 向导中不需要标签页
                  showActions={false}     // 向导有自己的操作按钮
                  showComments={true}     // 显示评语
                />
              </div>
            )}
            
            {/* 操作按钮 */}
            <Card className="wizard-result-actions">
              <Row justify="center" gutter={16}>
                <Col>
                  <Button size="large" onClick={() => navigate('/history')}>
                    历史记录
                  </Button>
                </Col>
                <Col>
                  <Button type="primary" size="large" onClick={() => navigate(`/essay/${essayData.id}/result`)}>
                    查看完整报告
                  </Button>
                </Col>
                <Col>
                  <Button size="large" onClick={() => window.location.reload()}>
                    批改新作文
                  </Button>
                </Col>
              </Row>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  // 移动端步骤指示器
  const MobileSteps = () => (
    <div className="mobile-steps">
      {steps.map((item, index) => (
        <div 
          key={item.title} 
          className={`mobile-step ${index === current ? 'active' : ''} ${index < current ? 'completed' : ''}`}
        >
          <div className="mobile-step-icon">
            {index < current ? <CheckCircleOutlined /> : index + 1}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="essay-wizard">
      <Card className="essay-wizard-card">
        {/* 桌面端步骤条 */}
        <div className="desktop-steps">
          <Steps current={current}>
            {steps.map((item) => (
              <Step key={item.title} title={item.title} description={item.description} icon={item.icon} />
            ))}
          </Steps>
        </div>
        
        {/* 移动端步骤条 */}
        <div className="mobile-steps-container">
          <MobileSteps />
        </div>
        
        <div className="steps-content">
          <Spin spinning={loading}>
            {renderStepContent()}
          </Spin>
        </div>
      </Card>
    </div>
  );
};

export default EssayWizard;
